import { useMutation } from 'react-query'
import { queryClient } from '../query-client'
import { requestProvider } from './requestProvider'
import { useDelete, useGet, usePost } from './reactQuery'

export const AIS_DEFAULT_MAP_COLOR = '#008000'

export const AIS_NAMED_COLOR_HEX: Record<number, string> = {
  0: '#FFFFFF',
  1: '#FFFF00',
  2: '#FF00FF',
  3: '#FF0000',
  4: '#C0C0C0',
  5: '#808080',
  6: '#808000',
  7: '#800080',
  8: '#800000',
  9: '#00FFFF',
  10: '#00FF00',
  11: '#008080',
  12: '#008000',
  13: '#0000FF',
  14: '#000080',
  15: '#000000',
}

export const resolveAisMapColor = (color?: string | number | null) => {
  if (typeof color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(color)) return color
  if (typeof color === 'number' && AIS_NAMED_COLOR_HEX[color]) return AIS_NAMED_COLOR_HEX[color]
  const parsed = Number(color)
  if (Number.isInteger(parsed) && AIS_NAMED_COLOR_HEX[parsed]) return AIS_NAMED_COLOR_HEX[parsed]
  return AIS_DEFAULT_MAP_COLOR
}

export interface IAisVessel {
  id: number
  mmsi: number
  name: string
  callSign?: string | null
  color: string
}

export interface IAisVesselFormValues {
  mmsi: number | string
  name: string
  callSign?: string
  color: string
}

export interface IAisVesselMapPosition {
  id: number
  mmsi: number
  name: string
  callSign?: string | null
  color: string
  lat: number
  lon: number
  sog: number
  cog: number
  hdg: number
  regDt: string
}

const baseUrl = (customerId: EntityId) => `customers/${customerId}/ais-vessels`

export const aisVesselEndpoints = {
  list(customerId: EntityId) {
    return baseUrl(customerId)
  },
  single(customerId: EntityId, id: EntityId) {
    return `${baseUrl(customerId)}/${id}`
  },
  mapPositions(customerId: EntityId) {
    return `${baseUrl(customerId)}/map-positions`
  },
}

export const invalidateAisVesselCache = {
  list(customerId: EntityId) {
    queryClient.invalidateQueries(aisVesselEndpoints.list(customerId))
    queryClient.invalidateQueries(aisVesselEndpoints.mapPositions(customerId))
  },
}

export const useGetAisVessels = (customerId: EntityId) =>
  useGet<IAisVessel[]>(aisVesselEndpoints.list(customerId), undefined, { enabled: !!customerId })

export const isValidAisMapPosition = (vessel: IAisVesselMapPosition) => {
  const lat = Number(vessel.lat)
  const lon = Number(vessel.lon)
  return Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0)
}

export const useGetAisVesselMapPositions = (customerId?: EntityId) =>
  useGet<IAisVesselMapPosition[]>(aisVesselEndpoints.mapPositions(customerId as EntityId), undefined, {
    enabled: !!customerId && Number(customerId) > 0,
  })

export const useAddAisVessel = (customerId: EntityId) => usePost(aisVesselEndpoints.list(customerId))

export const useUpdateAisVessel = (customerId: EntityId) =>
  useMutation(({ id, ...body }: IAisVesselFormValues & { id: number }) =>
    requestProvider.put(aisVesselEndpoints.single(customerId, id), body),
  )

export const useDeleteAisVessel = (customerId: EntityId) => useDelete(aisVesselEndpoints.list(customerId))
